// ignore-string-externalization

import React from 'react';
import styled from 'styled-components';
import { hexToRgba } from '../../../../features/src/color-conversion';
import { screenMdMin, spacer16, Type } from '@spotify-internal/encore-web';
import { IconArrowRight } from '@spotify-internal/encore-web-v6';
import {
  DELAYS,
  fadeInAnimation,
  withAnimationContext,
} from '@spotify-internal/frodor-modules';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { ButtonTertiaryAsLink, ImageAspectBox1x2 } from '../../ui';
import { ContentfulAsset } from '../../Contentful';
import { fixHref } from '../../utils';

type TwoUpSegmentType = {
  segmentHeadline?: string;
  segmentBody?: string;
  segmentLinkText?: string;
  segmentLinkUrl?: string;
  segmentImage?: ContentfulAsset;
};

const Segment = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: ${screenMdMin}) {
    flex-direction: column;
  }
`;

const Left = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;

  @media (max-width: ${screenMdMin}) {
    width: 100%;
    text-align: left;
  }
`;

const RightContainer = styled.div`
  width: 40%;

  @media (max-width: ${screenMdMin}) {
    width: 100%;
    display: flex;
    justify-content: center;
    img {
      padding-bottom: ${spacer16};
    }
  }
`;

const Right = styled(ImageAspectBox1x2)`
  width: 100%;
  padding: 0px ${spacer16};

  @media (max-width: ${screenMdMin}) {
    width: 50%;
  }
`;

const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    paddingBottom: '0.75em',
    variant: 'alto',
    weight: 'bold',
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
    padding-bottom: ${spacer16};

    @media (max-width: ${screenMdMin}) {
      padding-top: ${spacer16};
    }
  `,
);

const BodySection = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'ballad',
  })`
    ${fadeInAnimation(DELAYS.FOURTH)};
  `,
);

const SegmentTextLink = withAnimationContext(
  styled(ButtonTertiaryAsLink)`
    ${fadeInAnimation(DELAYS.FIFTH)};
    text-align: left;
    justify-content: left;

    color: ${({ theme }) => theme.box.text};
    &:hover:not(:focus):not(:disabled) {
      transform: none;
      color: ${({ theme }) => hexToRgba(theme.box.text, 0.5)};
    }
  `,
);

type TwoUpSegmentParams = {
  twoUpSegment: TwoUpSegmentType;
};

const TRACK_CAT = 'module-featuredContent2Up';

export const TwoUpSegment: React.FC<TwoUpSegmentParams> = ({
  twoUpSegment = {},
}) => {
  return (
    <Segment>
      <Left>
        <Headline>{twoUpSegment.segmentHeadline}</Headline>
        <BodySection>{twoUpSegment.segmentBody}</BodySection>
        {twoUpSegment.segmentLinkText && twoUpSegment.segmentLinkUrl && (
          <SegmentTextLink
            href={fixHref(twoUpSegment.segmentLinkUrl)}
            track={[TRACK_CAT, `Link - ${twoUpSegment.segmentLinkText}`]}
            iconTrailing={() => <IconArrowRight iconSize={16} />}
            condensed
          >
            {twoUpSegment.segmentLinkText}
          </SegmentTextLink>
        )}
      </Left>
      {twoUpSegment.segmentImage && (
        <RightContainer>
          <Right>
            <ContentfulImage asset={twoUpSegment.segmentImage} />
          </Right>
        </RightContainer>
      )}
    </Segment>
  );
};
